<template>
  <div class="negative-report-page">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/" class="breadcrumb">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>运营部</el-breadcrumb-item>
      <el-breadcrumb-item>差评日报</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 主内容区 -->
    <el-card class="main-card" shadow="hover">
      <div slot="header" class="card-header">
        <span>差评日报管理</span>
        <div class="header-actions">
          <el-button type="primary" size="small" icon="el-icon-document-add" @click="showGenerateDialog = true">手动生成</el-button>
          <el-button type="success" size="small" icon="el-icon-promotion"
            :disabled="selectedRows.length === 0" :loading="pushing"
            @click="handleBatchPush">
            批量推送<span v-if="selectedRows.length">（{{ selectedRows.length }}）</span>
          </el-button>
        </div>
      </div>

      <!-- 筛选工具栏 -->
      <div class="toolbar">
        <el-input
          v-model="query.keyword"
          placeholder="店铺名/项目名/群名"
          clearable
          size="small"
          style="width: 200px"
          @keyup.enter.native="handleSearch"
        ></el-input>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          size="small"
          style="width: 260px"
          value-format="yyyy-MM-dd"
          @change="handleSearch"
        ></el-date-picker>
        <el-select
          v-model="query.status"
          placeholder="状态"
          clearable
          size="small"
          style="width: 120px"
          @change="handleSearch"
        >
          <el-option label="待推送" value="unsent"></el-option>
          <el-option label="已推送" value="sent"></el-option>
          <el-option label="无中差评" value="none"></el-option>
        </el-select>
        <el-select
          v-model="query.brand"
          placeholder="品牌"
          clearable
          size="small"
          style="width: 120px"
          @change="handleSearch"
        >
          <el-option v-for="item in brandList" :key="item" :label="item" :value="item"></el-option>
        </el-select>
        <el-select
          v-model="query.currentOperatorId"
          placeholder="现阶段负责人"
          clearable
          filterable
          size="small"
          style="width: 150px"
          @change="handleSearch"
        >
          <el-option v-for="item in employeeOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 列表 -->
      <el-table
        :data="list"
        border
        size="small"
        v-loading="loading"
        :height="tableHeight"
        class="data-table"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="45"></el-table-column>
        <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
        <el-table-column prop="date" label="日期" width="110"></el-table-column>
        <el-table-column label="店铺名称" min-width="180">
          <template slot-scope="scope">
            <div style="font-size:14px;color:#333;font-weight:600;">{{ scope.row.projectName }}</div>
            <div style="color:#909399;font-size:12px;">{{ scope.row.storeName }}</div>
          </template>
        </el-table-column>
        <el-table-column label="品牌" width="100" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.brand" size="mini">{{ scope.row.brand }}</el-tag>
            <span v-else style="color: #c0c4cc">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="groupName" label="企微群" min-width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.groupName">{{ scope.row.groupName }}</span>
            <el-tag v-else type="info" size="mini">未绑定</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="差评" width="60" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.badCount > 0" style="color: #f56c6c; font-weight: bold;">{{ scope.row.badCount }}</span>
            <span v-else style="color: #ccc;">0</span>
          </template>
        </el-table-column>
        <el-table-column label="中评" width="60" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.neutralCount > 0" style="color: #e6a23c; font-weight: bold;">{{ scope.row.neutralCount }}</span>
            <span v-else style="color: #ccc;">0</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="statusTagType(scope.row.status)" size="mini">
              {{ statusLabel(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleDetail(scope.row)">查看详情</el-button>
            <el-button v-if="scope.row.status !== 'sent'" type="text" size="mini" @click="handleSinglePush(scope.row)" :loading="pushing">推送</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
        :current-page="pagenum"
        :page-sizes="[20, 50, 100]"
        :page-size="pagesize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        class="pagination"
      ></el-pagination>
    </el-card>

    <!-- 生成弹窗 -->
    <el-dialog title="手动生成差评日报" :visible.sync="showGenerateDialog" width="400px">
      <el-form label-width="80px">
        <el-form-item label="日期">
          <el-date-picker
            v-model="generateDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择日期"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="showGenerateDialog = false">取消</el-button>
        <el-button type="primary" @click="handleGenerate" :loading="generating">确认生成</el-button>
      </div>
    </el-dialog>

    <!-- 详情弹窗 -->
    <el-dialog title="差评日报详情" :visible.sync="showDetailDialog" width="700px" top="5vh">
      <div v-if="detailData" v-loading="detailLoading">
        <el-descriptions :column="2" border size="small" style="margin-bottom: 15px;">
          <el-descriptions-item label="店铺名称">{{ detailData.storeName }}</el-descriptions-item>
          <el-descriptions-item label="项目名称">{{ detailData.projectName }}</el-descriptions-item>
          <el-descriptions-item label="日期">{{ detailData.date }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="detailData.status === 'sent' ? 'info' : 'warning'" size="mini">
              {{ detailData.status === 'sent' ? '已推送' : '未推送' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="差评条数">{{ detailData.badCount }}</el-descriptions-item>
          <el-descriptions-item label="中评条数">{{ detailData.neutralCount }}</el-descriptions-item>
        </el-descriptions>

        <!-- 评论快照 -->
        <div v-if="detailData.comments && detailData.comments.length > 0">
          <h4 style="margin: 15px 0 8px;">评论快照（{{ detailData.comments.length }}条）</h4>
          <div v-for="(c, i) in detailData.comments" :key="i" class="comment-item">
            <el-tag size="mini" :type="c.score <= 2 ? 'danger' : 'warning'" style="margin-right: 8px;">
              {{ c.score <= 2 ? '差评' : '中评' }}{{ c.score }}分
            </el-tag>
            <span>{{ c.comment || '（无文字内容）' }}</span>
          </div>
        </div>

        <!-- 预渲染文案 -->
        <div v-if="detailData.badOnlyText || detailData.neutralOnlyText || detailData.mixedText" style="margin-top: 15px;">
          <h4 style="margin: 15px 0 8px;">预渲染推送文案</h4>
          <el-tabs>
            <el-tab-pane label="仅差评" v-if="detailData.badOnlyText">
              <pre class="preview-text">{{ detailData.badOnlyText }}</pre>
            </el-tab-pane>
            <el-tab-pane label="仅中评" v-if="detailData.neutralOnlyText">
              <pre class="preview-text">{{ detailData.neutralOnlyText }}</pre>
            </el-tab-pane>
            <el-tab-pane label="差评+中评" v-if="detailData.mixedText">
              <pre class="preview-text">{{ detailData.mixedText }}</pre>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { brandList } from '@/utility/staticData'

export default {
  name: 'NegativeCommentReport',
  data () {
    return {
      loading: false,
      pushing: false,
      generating: false,
      detailLoading: false,
      list: [],
      total: 0,
      pagenum: 1,
      pagesize: 20,
      selectedRows: [],
      brandList,
      employeeList: [],
      dateRange: null,
      query: {
        keyword: '',
        startDate: '',
        endDate: '',
        status: '',
        brand: '',
        currentOperatorId: ''
      },
      showGenerateDialog: false,
      generateDate: '',
      showDetailDialog: false,
      detailData: null
    }
  },
  computed: {
    employeeOptions () {
      return this.employeeList.filter(e => /运营师|运营主管|建店专员/.test(e.job))
    },
    tableHeight () {
      return window.innerHeight - 300
    }
  },
  created () {
    this.fetchList()
    this.getEmployees()
  },
  methods: {
    async fetchList () {
      this.loading = true
      try {
        const params = {
          pagenum: this.pagenum,
          pagesize: this.pagesize,
          ...this.query
        }
        if (this.dateRange && this.dateRange.length === 2) {
          params.startDate = this.dateRange[0]
          params.endDate = this.dateRange[1]
        } else {
          params.startDate = ''
          params.endDate = ''
        }
        const { data: res } = await this.$http.get('yunying/negativeCommentReport/list', { params })
        if (res.meta.status === 200) {
          this.list = res.data.list
          this.total = res.data.total
        } else {
          this.$message.error(res.meta.msg || '获取列表失败')
        }
      } catch (err) {
        this.$message.error('获取列表失败')
      } finally {
        this.loading = false
      }
    },
    async getEmployees () {
      try {
        const { data: res } = await this.$http.get('yunying/employees/list')
        if (res.meta.status === 200) {
          this.employeeList = res.data || []
        }
      } catch (err) {
        // 静默处理
      }
    },
    handleSearch () {
      this.pagenum = 1
      this.fetchList()
    },
    statusLabel (status) {
      const map = { unsent: '待推送', sent: '已推送', none: '无中差评' }
      return map[status] || status
    },
    statusTagType (status) {
      const map = { unsent: 'warning', sent: 'success', none: 'info' }
      return map[status] || ''
    },
    handleReset () {
      this.query.keyword = ''
      this.query.status = ''
      this.query.brand = ''
      this.query.currentOperatorId = ''
      this.dateRange = null
      this.pagenum = 1
      this.fetchList()
    },
    handleSizeChange (newSize) {
      this.pagesize = newSize
      this.pagenum = 1
      this.fetchList()
    },
    handlePageChange (page) {
      this.pagenum = page
      this.fetchList()
    },
    handleSelectionChange (rows) {
      this.selectedRows = rows
    },
    async handleGenerate () {
      if (!this.generateDate) {
        this.$message.warning('请选择日期')
        return
      }
      this.generating = true
      try {
        const { data: res } = await this.$http.post('yunying/negativeCommentReport/generate', { date: this.generateDate })
        if (res.meta.status === 200) {
          this.$message.success(`生成完成：新增${res.data.generated}条，更新${res.data.updated}条，无差评${res.data.noComment}条`)
          this.showGenerateDialog = false
          this.generateDate = ''
          this.fetchList()
        } else {
          this.$message.error(res.meta.msg || '生成失败')
        }
      } catch (err) {
        this.$message.error('生成失败')
      } finally {
        this.generating = false
      }
    },
    async handleBatchPush () {
      const ids = this.selectedRows.map(r => r._id)
      if (ids.length === 0) return
      await this.doPush(ids)
    },
    async handleSinglePush (row) {
      await this.doPush([row._id])
    },
    async doPush (ids) {
      this.pushing = true
      try {
        const { data: res } = await this.$http.post('yunying/negativeCommentReport/push', { ids })
        if (res.meta.status === 200) {
          const s = res.data
          if (s.queued > 0) {
            const parts = []
            parts.push(`已入队${s.queued}条`)
            if (s.failed > 0) parts.push(`入队失败${s.failed}条`)
            if (s.alreadySent > 0) parts.push(`已推送${s.alreadySent}条`)
            this.$message.success('已加入推送队列：' + parts.join('，') + '（将在数秒内发送）')
          } else {
            // 未实际入队，从 details 提取跳过原因
            const reasons = (s.details || []).filter(d => d.reason).map(d => d.reason)
            this.$message.warning(reasons.length ? `未入队：${reasons[0]}` : '未入队')
          }
          this.fetchList()
        } else {
          this.$message.error(res.meta.msg || '推送失败')
        }
      } catch (err) {
        this.$message.error('推送失败')
      } finally {
        this.pushing = false
      }
    },
    async handleDetail (row) {
      this.showDetailDialog = true
      this.detailData = null
      this.detailLoading = true
      try {
        const { data: res } = await this.$http.get(`yunying/negativeCommentReport/detail/${row._id}`)
        if (res.meta.status === 200) {
          this.detailData = res.data
        } else {
          this.$message.error(res.meta.msg || '获取详情失败')
          this.showDetailDialog = false
        }
      } catch (err) {
        this.$message.error('获取详情失败')
        this.showDetailDialog = false
      } finally {
        this.detailLoading = false
      }
    }
  }
}
</script>

<style scoped>
.negative-report-page {
  padding: 0 4px;
}

.breadcrumb {
  margin-bottom: 16px;
}

.main-card {
  border-radius: 8px;
  border: 1px solid #e8ecf1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* 工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 0 12px;
  flex-wrap: wrap;
}

/* 分页 */
.pagination {
  padding: 14px 0 0;
  text-align: right;
}

.comment-item {
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 6px;
  font-size: 13px;
  line-height: 1.6;
}
.preview-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  background: #f5f7fa;
  padding: 12px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.8;
  margin: 0;
}
</style>
